import {Box, Typography, alpha} from '@mui/material'
import {PhoneOutlined} from '@mui/icons-material'
import {SanitizedHtml} from '../../shared/SanitizedHtml'
import {MessageAttachment} from './MessageAttachment'

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i
const DEFAULT_FROM_NAME = 'Title Team'

export function MessageBubble({ content, date, isOurs, isRead, allowHtml, documentFileName, documentDeleted, companyIdOrderIdHash, fromName, phone }: {
  content: string;
  date: string;
  isOurs: boolean;
  isRead?: boolean;
  allowHtml?: boolean;
  documentFileName?: string;
  documentDeleted?: boolean;
  companyIdOrderIdHash: string;
  fromName?: string;
  phone?: string;
}) {
  const attachment = documentFileName ? (
    <MessageAttachment
      documentFileName={documentFileName}
      documentDeleted={documentDeleted}
      companyIdOrderIdHash={companyIdOrderIdHash}
      isOurs={isOurs}
    />
  ) : null

  const name = fromName?.trim() || DEFAULT_FROM_NAME
  const phoneDigits = phone?.replace(/[^\d+]/g, '')

  if (isOurs) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Box
          sx={{
            maxWidth: '85%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '16px 4px 16px 16px',
            p: '12px 16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
            ...(!allowHtml || !HTML_TAG_PATTERN.test(content) ? {whiteSpace: 'pre-wrap'} : {}),
          }}
        >
          {content && (allowHtml ? <SanitizedHtml html={content}/> : <span>{content}</span>)}
          {attachment}
        </Box>
        <Typography variant="teeny" sx={{ color: 'text.secondary', mt: 0.75, mr: 0.5, display: 'block' }}>
          You - {date} · <Box component="span" sx={{ color: isRead ? 'success.main' : 'inherit' }}>{isRead ? 'Read ✓' : 'Sent'}</Box>
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ maxWidth: '85%' }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '4px 16px 16px 16px',
            p: '12px 16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
            ...(!allowHtml || !HTML_TAG_PATTERN.test(content) ? {whiteSpace: 'pre-wrap'} : {}),
          }}
        >
          {content && (allowHtml ? <SanitizedHtml html={content}/> : <span>{content}</span>)}
          {attachment}
        </Box>
        <Typography variant="teeny" sx={{ color: 'text.secondary', mt: 0.75, ml: 0.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <span>{name} - {date}</span>
          {phoneDigits && (
            <Box
              component="a"
              href={`tel:${phoneDigits}`}
              title={phone}
              aria-label={`Call ${name}`}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                color: 'primary.main',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { bgcolor: theme => alpha(theme.palette.primary.main, 0.16) },
                // Grow the tap target to 24px tall without changing the pill's look
                '&::after': { content: '""', position: 'absolute', inset: '-4px -2px' },
              }}
            >
              <PhoneOutlined sx={{ fontSize: 12 }}/>
              Call
            </Box>
          )}
        </Typography>
      </Box>
    </Box>
  )
}
